import { getNotificationStatsApi, type NotificationStatsResponse } from 'api-validations/uhura'
import { useCallback, useMemo } from 'react'

import { useOrgCodeOrUndefined } from '../active-operation'
import { useApiClient, usePanoramaQuery, useQueryClient } from '../api-provider'
import { useNotificationSettings } from './useNotificationSettings'

export const useNotificationStats = () => {
  const orgCode = useOrgCodeOrUndefined()
  const { getOrgChannels } = useNotificationSettings()
  const enabledChannels = useMemo(
    () => (orgCode === undefined ? [] : getOrgChannels(orgCode)),
    [orgCode, getOrgChannels]
  )
  const hasEnabledChannels = enabledChannels.length > 0 && orgCode !== undefined
  const queryClient = useQueryClient()
  const apiClient = useApiClient()

  const notificationStats = usePanoramaQuery(
    getNotificationStatsApi,
    { orgCode: orgCode ?? '', channels: enabledChannels },
    { enabled: hasEnabledChannels }
  )

  const decrementUnreadCount = useCallback(() => {
    if (orgCode === undefined) return

    const statsQueryKey = [
      getNotificationStatsApi.pathFor({ orgCode, channels: enabledChannels }),
      apiClient
    ]

    queryClient.setQueryData<NotificationStatsResponse>(statsQueryKey, (oldData) => {
      if (!oldData) return oldData
      return {
        ...oldData,
        unread: Math.max(0, oldData.unread - 1),
        read: oldData.read + 1
      }
    })
  }, [orgCode, enabledChannels, apiClient, queryClient])

  return {
    ...notificationStats,
    unread: notificationStats.data?.unread ?? 0,
    decrementUnreadCount
  }
}
